
import { useEffect, useState } from "react";
import { Col, Form } from "react-bootstrap";
import { COLORS } from "utils/colors";

/**
 * SelectEdit is a controlled select dropdown component with styling and validation support.
 *
 * @memberof module:components/inputs
 * @param {Object} props
 * @param {string} [props.label] - Optional label text displayed above the select.
 * @param {string|number} props.value - The currently selected value.
 * @param {string} [props.type] - The input type, default is "select" (ignored since it's a select).
 * @param {boolean} [props.disabled=false] - Whether the select is disabled.
 * @param {string} [props.size] - Size of the select element (e.g., "sm", "lg").
 * @param {string} [props.placeholder] - Placeholder text for the select element.
 * @param {string} [props.error] - Error message or flag to style the select as invalid.
 * @param {React.ReactNode} props.children - Option elements to be rendered inside the select.
 * @param {function} [props.onFocus] - Callback for when the select gains focus.
 * @param {function} props.onChange - Callback for when the selected value changes.
 * @param {function} [props.onKeyDown] - Callback for key down events on the select.
 *
 * @returns {JSX.Element} The rendered select input component.
 */
const SelectEdit = ({
  label,
  value,
  type,
  disabled,
  size,
  placeholder,
  error,
  children,
  onFocus,
  onChange,
  onKeyDown,
}) => {
  const [isFocused, setIsFocus] = useState(false);
  const [valueEdit, setValue] = useState(value);
  const [errorEdit, setError] = useState(error);

  useEffect(() => {
    setValue(value);
  }, [value]);

  useEffect(() => {
    setError(error);
  }, [error]);

  return (
    <>
      {label&&<Form.Label
        column
        style={{
          fontWeight: isFocused ? "bold" : "normal",
          color: isFocused
            ? COLORS.selectedColorTheme
            : error
            ? "red"
            : COLORS.greyFontColor,
          opacity: disabled ? 0.5 : 1,
        }}
      >
        {label}
      </Form.Label>}
      <Col>
        <Form.Select
          type={"select"}
          {...(size && { size })}
          autoComplete="off"
          disabled={disabled ? true : false}
          placeholder={placeholder ? placeholder : ""}
          value={valueEdit}
          style={{
            color: isFocused
              ? COLORS.selectedColorTheme
              : error
              ? "red"
              : COLORS.greyFontColor,
            background: isFocused
              ? "url(/icons/arrow-down-focused.png) no-repeat right " +
                COLORS.ligthGrey
              : "url(/icons/arrow-down.png) no-repeat right " +
                COLORS.ligthGrey,
            border: isFocused
              ? "2px solid " + COLORS.selectedColorTheme
              : error
              ? "2px solid red"
              : "2px solid " + COLORS.greyFontColor,
            opacity: disabled ? 0.5 : 1,
            WebkitAppearance: "none",
            appearance: "none",
          }}
          onFocus={(e) => {
            setIsFocus(true);
            onFocus && onFocus(e);
          }}
          onBlur={(e) => {
            setIsFocus(false);
          }}
          onChange={(e) => {
            setValue(e.target.value);
            onChange(e);
          }}
          onKeyDown={(e) => {
            onKeyDown && onKeyDown(e);
          }}
        >
          {children}
        </Form.Select>
      </Col>
    </>
  );
};

export default SelectEdit;
